import { Timestamp } from '@bufbuild/protobuf/wkt'
import type { ReactNode } from 'react'

import { AssessmentScore } from '@/gen/assessment/v1/assessment_score_pb'
import { User } from '@/gen/customer/management/v1/service_pb'
import { AssessmentDecision } from '@/gen/inventory/v1/assessment_decision_pb'

import { formatTimestamp } from '@/lib/date/date'

import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { AssessmentDecisionBadge } from '@/components/shared/assessment-decision-badge/assessment-decision-badge'
import { AssessmentScoreBadge } from '@/components/shared/assessment-score-badge/assessment-score-badge'
import { UserScoreDisplay } from '@/components/shared/user-score-display/user-score-display'

import { ASSESSMENT_REPORT_HEADER_LABELS } from './labels'

export type AssessmentReportHeaderProps = {
  companyName: string
  score: AssessmentScore
  projectName?: string
  createdAt?: Timestamp
  actions?: ReactNode
  decision?: AssessmentDecision
  concludedBy?: User
  concludedAt?: Timestamp
  decidedBy?: User
  decidedAt?: Timestamp
}

type SubtitleUserProps = {
  user: User
  at?: Timestamp
}

const SubtitleUser = ({ user, at }: SubtitleUserProps) => (
  <div className='flex items-center gap-2'>
    <UserScoreDisplay userId={user.id} userName={user.name} />
    {at && (
      <span className='text-main-foreground-tertiary text-body-small'>, {formatTimestamp(at)}</span>
    )}
  </div>
)

type HeaderItemProps = {
  label: ReactNode
  children: ReactNode
}

const HeaderItem = ({ label, children }: HeaderItemProps) => (
  <div className='group/header-item flex items-center'>
    <Separator orientation='vertical' className='mx-4 min-h-10 group-first/header-item:hidden' />
    <div className='flex h-12 flex-col justify-between'>
      <span className='flex items-center gap-1 text-main-foreground-secondary text-label-small'>
        {label}
      </span>
      {children}
    </div>
  </div>
)

export const AssessmentReportHeader = ({
  companyName,
  projectName,
  createdAt,
  actions,
  decision,
  concludedBy,
  concludedAt,
  decidedBy,
  decidedAt,
  score,
}: AssessmentReportHeaderProps) => {
  const showDecision = decision !== undefined && decision !== AssessmentDecision.UNSPECIFIED
  const showDecidedBy = !!decidedBy && showDecision

  return (
    <div className='pb-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-heading-large'>
          {companyName} - {projectName ? `${projectName} - ` : ''}
          {formatTimestamp(createdAt, 'monthDay')}
        </h2>
        {actions}
      </div>

      <div className='mt-4 flex flex-row items-center gap-2'>
        {concludedBy && (
          <HeaderItem label={ASSESSMENT_REPORT_HEADER_LABELS.CONCLUDED_BY}>
            <SubtitleUser user={concludedBy} at={concludedAt} />
          </HeaderItem>
        )}
        {showDecidedBy && (
          <HeaderItem
            label={
              <>
                <AssessmentDecisionBadge decision={decision} disableIcon={true} />
                {ASSESSMENT_REPORT_HEADER_LABELS.BY}
              </>
            }
          >
            <SubtitleUser user={decidedBy} at={decidedAt} />
          </HeaderItem>
        )}
        {score !== AssessmentScore.UNSPECIFIED && (
          <HeaderItem label={ASSESSMENT_REPORT_HEADER_LABELS.SCORE}>
            <AssessmentScoreBadge assessmentScore={score} />
          </HeaderItem>
        )}
      </div>
    </div>
  )
}
